import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Grid,
  Stack,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DialogActions,
  Link,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useState, useEffect } from 'react';
import axios from 'axios';
import NotFoundPage from './NotFoundPage';
import CarCard from '../components/CarCard';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FaxIcon from '@mui/icons-material/Print';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmailIcon from '@mui/icons-material/Email';
import CarFilterBar from '../components/CarFilterBar';
import CallIcon from '@mui/icons-material/Call';
import { clearAuth } from '../utils/storage';
import axiosInstance from '../services/axiosInstance';
import OtherBranchDialog from '../components/OtherBranchDialog';



interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const DealerVehicleListing = ({ mode, setMode }: HomePageProps) => {
  // 1. React & Router setup
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // 2. Search params (early so they can be used in filtering/sorting)
  const searchParams = new URLSearchParams(location.search);

  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(',').map(Number) : [];
  };

  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');

  const statusIdString = searchParams.get('statusId'); // e.g. "VSL,VPB"
  const statusID = statusIdString ? statusIdString.split(',') : [];

  const parsePriceParams = () => {
    const min = searchParams.get('minAskingPrice');
    const max = searchParams.get('maxAskingPrice');
    return {
      minAskingPrice: min ? Number(min) : null,
      maxAskingPrice: max ? Number(max) : null,
    };
  };
  const { minAskingPrice, maxAskingPrice } = parsePriceParams();

  const parseYearParams = () => {
    const min = searchParams.get('minManufacturingYear');
    const max = searchParams.get('maxManufacturingYear');
    return {
      minManufacturingYear: min ? Number(min) : null,
      maxManufacturingYear: max ? Number(max) : null,
    };
  };
  const { minManufacturingYear, maxManufacturingYear } = parseYearParams();

  const parseMileageParams = () => {
    const min = searchParams.get('minMileage');
    const max = searchParams.get('maxMileage');
    return {
      minMileage: min ? Number(min) : null,
      maxMileage: max ? Number(max) : null,
    };
  };
  const { minMileage, maxMileage } = parseMileageParams();

  const parseDepreciationParams = () => {
    const min = searchParams.get('minDepreciation');
    const max = searchParams.get('maxDepreciation');
    return {
      minDepreciationValue: min ? Number(min) : null,
      maxDepreciationValue: max ? Number(max) : null,
    };
  };
  const { minDepreciationValue, maxDepreciationValue } = parseDepreciationParams();

  const sortKeys = searchParams.get('sortKeys')?.split(',') || [];
  const sortDirs = searchParams.get('sortDirs')?.split(',') as ('asc' | 'desc')[];
  const sortList = sortKeys.map((key, i) => ({
    key,
    direction: sortDirs[i] || 'asc',
  }));

  const vehicleModel = searchParams.get('vehicleModel') || '';

  // 3. State declarations
  const [isLoggedIn, setLoggedin] = useState(true);
  const [vehicle, setVehicleList] = useState<any[] | null>(null);
  const [vehicleDealerInfo, setDealerInfo] = useState<any | null>(null);
  const [otherBranch, setOtherBranch] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);


  // 4. Fetch function
  const payload = {
    dealerId: id,
    vehicleTransmissionTypeIds,
    vehicleFuelTypeIds,
    vehicleBrandIds,
    vehicleClassIds,
    vehicleBodyTypeIds,
    minAskingPrice,
    maxAskingPrice,
    minManufacturingYear,
    maxManufacturingYear,
    minMileage,
    maxMileage,
    minDepreciationValue,
    maxDepreciationValue,
    statusID,
  };
  const fetchVehicle = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        '/CustomerVehicle/VehicleGetListPublished',
        payload
      );

      const data = response.data as {
        responseData?: { vehicleList?: any[] };
      };

      setVehicleList(data.responseData?.vehicleList || []);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch vehicle data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDealer = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/CustomerVehicle/DealerGetByCompany/${id}`
      );

      const data = response.data as {
        responseData?: any; // you can replace `any` with a proper Dealer type if available
      };
      const branchList = data.responseData?.otherBranch || [];

      setDealerInfo(data.responseData || null); // e.g., set state with dealer info
      setOtherBranch(branchList);

    } catch (err) {
      console.error(err);
      setError('Failed to fetch dealer information.');
    } finally {
      setLoading(false);
    }
  };

  // 5. Effect hook to fetch data
  useEffect(() => {
    fetchVehicle();
    fetchDealer();
  }, [location.search, id]); // ✅ now fetches only when filters/search in URL change

  // 6. Error state handling
  if (error) return <NotFoundPage />;

  // 7. Filter and sort list
  const filteredList =
    vehicle?.filter((v) => {
      const brandModel = `${v.vehicleBrand} ${v.vehicleModel || ''}`.toLowerCase();
      const matchesSearchQuery = brandModel.includes(searchQuery.toLowerCase());
      const matchesVehicleModel =
        !vehicleModel || brandModel.includes(vehicleModel.toLowerCase());

      return matchesSearchQuery && matchesVehicleModel;
    }) || [];

  const sortedList = [...filteredList].sort((a, b) => {
    for (let i = 0; i < sortList.length; i++) {
      const { key, direction } = sortList[i];
      let valA = a[key];
      let valB = b[key];

      // Convert numeric strings to actual numbers
      if (typeof valA === 'string' && !isNaN(Number(valA))) valA = Number(valA);
      if (typeof valB === 'string' && !isNaN(Number(valB))) valB = Number(valB);

      if (valA == null || valB == null) continue;

      if (typeof valA === 'string' && typeof valB === 'string') {
        const compare = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
        if (compare !== 0) return direction === 'asc' ? compare : -compare;
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        const compare = valA - valB;
        if (compare !== 0) return direction === 'asc' ? compare : -compare;
      } else {
        const compare = String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base' });
        if (compare !== 0) return direction === 'asc' ? compare : -compare;
      }
    }
    return 0;
  });

  // 8. Pagination logic
  const totalPages = Math.ceil(sortedList.length / itemsPerPage);
  const paginatedList = sortedList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get(key);

    let updatedValues: string[] = [];

    if (existing) {
      const values = existing.split(',');
      if (values.includes(value)) {
        // Deselect: remove the value
        updatedValues = values.filter((v) => v !== value);
      } else {
        // Select: add the value
        updatedValues = [...values, value];
      }

      if (updatedValues.length > 0) {
        currentParams.set(key, updatedValues.join(','));
      } else {
        currentParams.delete(key); // Remove key if nothing selected
      }
    } else {
      // No previous value: add the first one
      updatedValues = [value];
      currentParams.set(key, value);
    }

    // Reset pagination
    currentParams.delete('page');

    // Apply new filters
    navigate(`/dealer-vehicles/${id}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`);

  };

  const googleMapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    vehicleDealerInfo?.address
  )}&output=embed`;


  const handleGmailOpen = (email: string) => {
    const subject = encodeURIComponent('Enquiry from Work Horse website');
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in one of the vehicles listed on the Work Horse website.\n\nCould you please provide more details or arrange for a viewing?\n\nThank you.`
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };


  return (
    <>
      <Header  />
      <Box p={2} display="flex" justifyContent="center">
        {loading ? (
          <Paper
            elevation={2}
            sx={{
              p: 2,
              maxWidth: 1300,
              width: '100%',
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Paper>
        ) : (
          <Box sx={{ maxWidth: 1300, width: '100%' }}>
            {vehicleDealerInfo && (
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 2, md: 4 },
                  mb: 4,
                  borderRadius: 3,
                  bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
                }}
              >
                <Grid container spacing={6}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    {vehicleDealerInfo && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          p: 2,
                        }}
                      >
                        {/* Dealer Header */}
                        {(vehicleDealerInfo.imageUrl || vehicleDealerInfo.dealerName) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {vehicleDealerInfo.imageUrl && (
                              <Box
                                component="img"
                                src={vehicleDealerInfo.imageUrl}
                                alt={vehicleDealerInfo.dealerName || 'Dealer Logo'}
                                sx={{
                                  width: 100,
                                  height: 100,
                                  borderRadius: 1,
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                            {vehicleDealerInfo.dealerName && (
                              <Typography fontWeight={700}
                                fontSize="1.25rem"
                                sx={{ cursor: 'pointer', color: 'text.primary' }}>
                                {vehicleDealerInfo.dealerName}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Info Rows */}
                        {vehicleDealerInfo.address && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOnIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {vehicleDealerInfo.address}
                            </Typography>
                          </Box>
                        )}

                        {otherBranch?.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} gap={1} onClick={() => setBranchDialogOpen(true)}>
                            <StorefrontIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography
                              variant="body2"
                              color="primary"
                            >
                              {otherBranch.length} more branch{otherBranch.length > 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        )}
                        <OtherBranchDialog
                          branchDialogOpen={branchDialogOpen}
                          setBranchDialogOpen={setBranchDialogOpen}
                          otherBranch={otherBranch}
                          vehicleDealerInfo={vehicleDealerInfo}
                        />

                        {vehicleDealerInfo.officeNumber && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CallIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {vehicleDealerInfo.officeNumber}
                            </Typography>
                          </Box>
                        )}

                        {vehicleDealerInfo.faxNumber && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <FaxIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {vehicleDealerInfo.faxNumber}
                            </Typography>
                          </Box>
                        )}

                        {vehicleDealerInfo.contactNumber && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon fontSize="small" color="action" />
                            <a
                              href={`tel:${vehicleDealerInfo.contactNumber}`}
                              style={{
                                fontSize: 14,
                                textDecoration: 'none',
                                color: '#1976d2', // or use theme primary color
                              }}
                            >
                              {vehicleDealerInfo.contactNumber}
                            </a>
                          </Box>
                        )}

                        {(vehicleDealerInfo.website || vehicleDealerInfo.dealerEmail) && (
                          <Box display="flex" flexDirection="column" gap={1} mt={1}>
                            {/* Show website if available */}
                            {vehicleDealerInfo.website && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LanguageIcon fontSize="small" color="action" />
                                <a
                                  href={vehicleDealerInfo.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    fontSize: 14,
                                    textDecoration: 'none',
                                    color: '#1976d2',
                                  }}
                                >
                                  {vehicleDealerInfo.website}
                                </a>
                              </Box>
                            )}

                            {/* Show email if available */}
                            {vehicleDealerInfo.dealerEmail && (
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)}
                              >
                                <EmailIcon fontSize="small" color="action" />
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 14,
                                    color: '#1976d2',
                                    textDecoration: 'underline',
                                  }}
                                >
                                  {vehicleDealerInfo.dealerEmail}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}



                        {/* Contact Button */}
                        {vehicleDealerInfo.dealerEmail && (
                          <Box mt={1}>
                            <Box
                              onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                bgcolor: 'primary.main',
                                color: 'white',
                                py: 1,
                                px: 3,
                                borderRadius: 2,
                                fontSize: 14,
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                },
                              }}
                            >
                              <EmailIcon fontSize="small" />
                              Contact Seller
                            </Box>
                          </Box>
                        )}

                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, md: 7 }} >
                    <Box sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
                      <iframe
                        title="Dealer Location Map"
                        width="100%"
                        height="300"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={googleMapSrc}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Box display="flex" flexDirection="column" mb={2} gap={2}>
              <Typography variant="h6">
                🚗 Vehicles Listed by{' '}
                <Box component="span" sx={{ color: 'text.primary' }} display="inline">
                  {vehicleDealerInfo?.dealerName || 'Dealer'}
                </Box>
              </Typography>

              <CarFilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                url={`/dealer-vehicles/${id}`}
                onFilterChange={handleFilterChange}
              />
            </Box>


            {filteredList.length > 0 ? (
              <>
                <Stack spacing={2}>
                  {paginatedList.map((v) => (
                    <CarCard key={v.vehicleId} vehicle={v} viewMode="list" />
                  ))}
                </Stack>

                <Box mt={4} display="flex" flexDirection="column" alignItems="center">
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, filteredList.length)} of{' '}
                    {filteredList.length} Vehicles
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                  />
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={6}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: mode === 'dark' ? '#1e1e1e' : '#fafafa',
                }}
              >
                <Box
                  component="img"
                  src="https://cdn-icons-png.flaticon.com/512/747/747376.png"
                  alt="No Vehicles"
                  sx={{ width: 120, height: 120, mb: 2 }}
                />
                <Typography variant="h6" gutterBottom color="text.secondary">
                  No vehicles available from this dealer
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Check back later or browse other dealers for more vehicles.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/vehicle-listing')}>
                  View All Vehicle
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Footer />
    </>
  );
};

export default DealerVehicleListing;
